import type { FontStyleKey } from './applyFontStyle'

export type DecorationStyle = 'underline' | 'line-through' | 'overline'
export type VerticalAlign = 'super' | 'sub' | null

export class StyleModel {
	bold: boolean = false
	italic: boolean = false
	decorations: Set<DecorationStyle> = new Set()
	fontColor: string | null = null
	backgroundColor: string | null = null
	fontSize: string | null = null
	fontFamily: string | null = null
	verticalAlign: VerticalAlign = null
	originalFontSize: string | null = null

	static fromSpan(span: HTMLSpanElement): StyleModel {
		const model = new StyleModel()
		
		model.bold = span.style.fontWeight === 'bold' || span.style.fontWeight === '700'
		model.italic = span.style.fontStyle === 'italic'
		
		const textDeco = span.style.textDecoration || ''
		const textDecoLine = span.style.textDecorationLine || ''
		const combined = `${textDeco} ${textDecoLine}`.toLowerCase()
		
		if (combined.includes('underline')) model.decorations.add('underline')
		if (combined.includes('line-through')) model.decorations.add('line-through')
		if (combined.includes('overline')) model.decorations.add('overline')
		
		// 색상 값 정규화: rgb(...)를 그대로 저장하거나 빈 값이면 null
		const color = span.style.color || ''
		const bgColor = span.style.backgroundColor || ''
		model.fontColor = color ? color : null
		model.backgroundColor = bgColor ? bgColor : null
		
		for (let i = 0; i < span.classList.length; i++) {
			const cls = span.classList[i]
			if (cls && !cls.startsWith('editor-')) {
				model.fontFamily = cls
				break
			}
		}
		
		const vAlign = span.style.verticalAlign
		// data-original-fontsize 속성 읽기 (verticalAlign 유무와 관계없이)
		const originalFontSizeAttr = span.getAttribute('data-original-fontsize')
		
		if (originalFontSizeAttr) {
			model.originalFontSize = originalFontSizeAttr
		}
		
		if (vAlign === 'super' || vAlign === 'sub') {
			model.verticalAlign = vAlign
			// 윗첨자/아래첨자는 fontSize를 자동 계산하므로, 모델에는 저장하지 않음
			model.fontSize = null
		} else {
			model.fontSize = span.style.fontSize || null
		}
		
		return model
	}

	applyToSpan(span: HTMLSpanElement): void {
		if (this.bold) {
			span.style.fontWeight = 'bold'
		} else {
			span.style.removeProperty('font-weight')
		}
		
		if (this.italic) {
			span.style.fontStyle = 'italic'
		} else {
			span.style.removeProperty('font-style')
		}
		
		// text-decoration: 항상 모든 관련 속성을 먼저 제거 후 재설정
		span.style.removeProperty('text-decoration')
		span.style.removeProperty('text-decoration-line')
		span.style.removeProperty('text-decoration-style')
		span.style.removeProperty('text-decoration-color')
		span.style.removeProperty('text-decoration-thickness')
		
		if (this.decorations.size > 0) {
			const decoArray = Array.from(this.decorations).sort()
			const decoValue = decoArray.join(' ')
			span.style.setProperty('text-decoration-line', decoValue, 'important')
		}
		
		if (this.fontColor) {
			const color = (this.fontColor.startsWith('#') || this.fontColor.startsWith('rgb')) 
				? this.fontColor 
				: `#${this.fontColor}`
			span.style.setProperty('color', color, 'important')
		} else {
			span.style.removeProperty('color')
		}
		
		if (this.backgroundColor) {
			const bg = (this.backgroundColor.startsWith('#') || this.backgroundColor.startsWith('rgb')) 
				? this.backgroundColor 
				: `#${this.backgroundColor}`
			span.style.setProperty('background-color', bg, 'important')
			span.setAttribute('data-editor-bg', '1')
		} else {
			span.style.removeProperty('background-color')
			span.removeAttribute('data-editor-bg')
		}
		
		if (this.fontSize) {
			const size = String(this.fontSize).replace('px', '')
			span.style.setProperty('font-size', `${size}px`, 'important')
		} else {
			span.style.removeProperty('font-size')
		}
		
		// Remove all existing font family classes first
		for (let i = span.classList.length - 1; i >= 0; i--) {
			const cls = span.classList[i]
			if (cls && !cls.startsWith('editor-')) {
				span.classList.remove(cls)
			}
		}
		
		// Then add the new font family class if provided
		if (this.fontFamily) {
			span.classList.add(this.fontFamily)
		}
		
		if (this.verticalAlign === 'super') {
			const blockEl = span.closest('.editor--block') as HTMLElement | null
			let baseFontSize = 14
			if (blockEl) {
				const computed = window.getComputedStyle(blockEl)
				baseFontSize = parseFloat(computed.fontSize) || 14
			}
			
			const currentFontSize = span.style.fontSize || this.fontSize
			if (currentFontSize && !this.originalFontSize) {
				this.originalFontSize = currentFontSize
				span.setAttribute('data-original-fontsize', currentFontSize)
			} else if (this.originalFontSize) {
				span.setAttribute('data-original-fontsize', this.originalFontSize)
			} else {
				span.setAttribute('data-original-fontsize', `${baseFontSize}px`)
			}
			
			const reducedSize = Math.round(baseFontSize * 0.75 * 10) / 10
			span.style.setProperty('vertical-align', 'super')
			span.style.setProperty('font-size', `${reducedSize}px`)
		} else if (this.verticalAlign === 'sub') {
			const blockEl = span.closest('.editor--block') as HTMLElement | null
			let baseFontSize = 14
			if (blockEl) {
				const computed = window.getComputedStyle(blockEl)
				baseFontSize = parseFloat(computed.fontSize) || 14
			}
			
			const currentFontSize = span.style.fontSize || this.fontSize
			if (currentFontSize && !this.originalFontSize) {
				this.originalFontSize = currentFontSize
				span.setAttribute('data-original-fontsize', currentFontSize)
			} else if (this.originalFontSize) {
				span.setAttribute('data-original-fontsize', this.originalFontSize)
			} else {
				span.setAttribute('data-original-fontsize', `${baseFontSize}px`)
			}
			
			const reducedSize = Math.round(baseFontSize * 0.75 * 10) / 10
			span.style.setProperty('vertical-align', 'sub')
			span.style.setProperty('font-size', `${reducedSize}px`)
		} else {
			span.style.removeProperty('vertical-align')
			
			const originalFontSizeAttr = span.getAttribute('data-original-fontsize')
			span.removeAttribute('data-original-fontsize')
			
			if (this.originalFontSize) {
				const size = String(this.originalFontSize).replace('px', '')
				span.style.setProperty('font-size', `${size}px`, 'important')
			} else if (this.fontSize) {
				const size = String(this.fontSize).replace('px', '')
				span.style.setProperty('font-size', `${size}px`, 'important')
			} else {
				span.style.removeProperty('font-size')
			}
		}
	}

	merge(other: StyleModel): StyleModel {
		const merged = new StyleModel()
		merged.bold = this.bold || other.bold
		merged.italic = this.italic || other.italic
		merged.decorations = new Set([...this.decorations, ...other.decorations])
		merged.fontColor = other.fontColor || this.fontColor
		merged.backgroundColor = other.backgroundColor || this.backgroundColor
		merged.fontSize = other.fontSize || this.fontSize
		merged.fontFamily = other.fontFamily || this.fontFamily
		merged.verticalAlign = other.verticalAlign || this.verticalAlign
		merged.originalFontSize = other.originalFontSize || this.originalFontSize
		return merged
	}

	toggle(style: FontStyleKey, value = ''): StyleModel {
		const toggled = this.clone()
		
		switch (style) {
			case 'bold':
				toggled.bold = !toggled.bold
				break
			case 'italic':
				toggled.italic = !toggled.italic
				break
			case 'underline':
				if (toggled.decorations.has('underline')) {
					toggled.decorations.delete('underline')
				} else {
					toggled.decorations.add('underline')
				}
				break
			case 'line-through':
				if (toggled.decorations.has('line-through')) {
					toggled.decorations.delete('line-through')
				} else {
					toggled.decorations.add('line-through')
				}
				break
			case 'fontcolor':
				toggled.fontColor = toggled.fontColor ? null : value
				break
			case 'backgroundcolor':
				toggled.backgroundColor = toggled.backgroundColor ? null : value
				break
			case 'fontsize':
				toggled.fontSize = toggled.fontSize ? null : value
				break
			case 'fontfamily':
				toggled.fontFamily = toggled.fontFamily ? null : value
				break
			case 'superscript':
				if (toggled.verticalAlign === 'super') {
					toggled.verticalAlign = null
					if (toggled.originalFontSize) {
						toggled.fontSize = toggled.originalFontSize
						toggled.originalFontSize = null
					} else {
						toggled.fontSize = null
					}
				} else {
					toggled.verticalAlign = 'super'
				}
				break
			case 'subscript':
				if (toggled.verticalAlign === 'sub') {
					toggled.verticalAlign = null
					if (toggled.originalFontSize) {
						toggled.fontSize = toggled.originalFontSize
						toggled.originalFontSize = null
					} else {
						toggled.fontSize = null
					}
				} else {
					toggled.verticalAlign = 'sub'
				}
				break
		}
		
		return toggled
	}

	apply(style: FontStyleKey, value = ''): StyleModel {
		const applied = this.clone()
		
		switch (style) {
			case 'bold':
				applied.bold = true
				break
			case 'italic':
				applied.italic = true
				break
			case 'underline':
				applied.decorations.add('underline')
				break
			case 'line-through':
				applied.decorations.add('line-through')
				break
			case 'fontcolor':
				applied.fontColor = value
				break
			case 'backgroundcolor':
				applied.backgroundColor = value
				break
			case 'fontsize':
				applied.fontSize = value
				break
			case 'fontfamily':
				applied.fontFamily = value
				break
			case 'superscript':
				applied.verticalAlign = 'super'
				break
			case 'subscript':
				applied.verticalAlign = 'sub'
				break
		}
		
		return applied
	}

	remove(style: FontStyleKey, value = ''): StyleModel {
		const removed = this.clone()
		
		switch (style) {
			case 'bold':
				removed.bold = false
				break
			case 'italic':
				removed.italic = false
				break
			case 'underline':
				removed.decorations.delete('underline')
				break
			case 'line-through':
				removed.decorations.delete('line-through')
				break
			case 'fontcolor':
				removed.fontColor = null
				break
			case 'backgroundcolor':
				removed.backgroundColor = null
				break
			case 'fontsize':
				removed.fontSize = null
				break
			case 'fontfamily':
				removed.fontFamily = null
				break
			case 'superscript':
			case 'subscript':
				removed.verticalAlign = null
				if (removed.originalFontSize) {
					removed.fontSize = removed.originalFontSize
					removed.originalFontSize = null
				} else {
					removed.fontSize = null
				}
				break
		}
		
		return removed
	}

	hasStyle(style: FontStyleKey, value = ''): boolean {
		switch (style) {
			case 'bold':
				return this.bold
			case 'italic':
				return this.italic
			case 'underline':
				return this.decorations.has('underline')
			case 'line-through':
				return this.decorations.has('line-through')
			case 'fontcolor':
				return !!this.fontColor
			case 'backgroundcolor':
				return !!this.backgroundColor
			case 'fontsize':
				return !!this.fontSize && (value ? this.fontSize.replace('px', '') === String(value) : true)
			case 'fontfamily':
				return value ? this.fontFamily === value : !!this.fontFamily
			case 'superscript':
				return this.verticalAlign === 'super'
			case 'subscript':
				return this.verticalAlign === 'sub'
			default:
				return false
		}
	}

	isEmpty(): boolean {
		return (
			!this.bold &&
			!this.italic &&
			this.decorations.size === 0 &&
			!this.fontColor &&
			!this.backgroundColor &&
			!this.fontSize &&
			!this.fontFamily &&
			!this.verticalAlign
		)
	}

	equals(other: StyleModel): boolean {
		if (this.bold !== other.bold) return false
		if (this.italic !== other.italic) return false
		if (this.fontColor !== other.fontColor) return false
		if (this.backgroundColor !== other.backgroundColor) return false
		if (this.fontSize !== other.fontSize) return false
		if (this.fontFamily !== other.fontFamily) return false
		if (this.verticalAlign !== other.verticalAlign) return false
		if (this.originalFontSize !== other.originalFontSize) return false
		
		if (this.decorations.size !== other.decorations.size) return false
		for (const deco of this.decorations) {
			if (!other.decorations.has(deco)) return false
		}
		
		return true
	}

	clone(): StyleModel {
		const cloned = new StyleModel()
		cloned.bold = this.bold
		cloned.italic = this.italic
		cloned.decorations = new Set(this.decorations)
		cloned.fontColor = this.fontColor
		cloned.backgroundColor = this.backgroundColor
		cloned.fontSize = this.fontSize
		cloned.fontFamily = this.fontFamily
		cloned.verticalAlign = this.verticalAlign
		cloned.originalFontSize = this.originalFontSize
		return cloned
	}

	toDebugString(): string {
		const parts: string[] = []
		if (this.bold) parts.push('bold')
		if (this.italic) parts.push('italic')
		if (this.decorations.size > 0) parts.push(`deco:[${Array.from(this.decorations).join(',')}]`)
		if (this.fontColor) parts.push(`color:${this.fontColor}`)
		if (this.backgroundColor) parts.push(`bg:${this.backgroundColor}`)
		if (this.fontSize) parts.push(`size:${this.fontSize}`)
		if (this.fontFamily) parts.push(`font:${this.fontFamily}`)
		if (this.verticalAlign) parts.push(`align:${this.verticalAlign}`)
		return parts.length > 0 ? parts.join(' ') : 'empty'
	}
}
